import { useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { cn } from '../utils/cn';
import type { ToastItem, ToastSeverity } from '../../types/toast';

const severityConfig: Record<
  ToastSeverity,
  {
    icon: typeof AlertCircle;
    bgClass: string;
    borderClass: string;
    textClass: string;
    iconClass: string;
    progressClass: string;
  }
> = {
  error: {
    icon: AlertCircle,
    bgClass: 'bg-feedback-error/10 dark:bg-feedback-error/20',
    borderClass: 'border-feedback-error/30',
    textClass: 'text-feedback-error',
    iconClass: 'text-feedback-error',
    progressClass: 'bg-feedback-error',
  },
  warning: {
    icon: AlertTriangle,
    bgClass: 'bg-feedback-warning/10 dark:bg-feedback-warning/20',
    borderClass: 'border-feedback-warning/30',
    textClass: 'text-feedback-warning',
    iconClass: 'text-feedback-warning',
    progressClass: 'bg-feedback-warning',
  },
  info: {
    icon: CheckCircle,
    bgClass: 'bg-feedback-success/10 dark:bg-feedback-success/20',
    borderClass: 'border-feedback-success/30',
    textClass: 'text-feedback-success',
    iconClass: 'text-feedback-success',
    progressClass: 'bg-feedback-success',
  },
};

export interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

export function Toast({ toast, onDismiss }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const config = severityConfig[toast.severity];
  const Icon = config.icon;

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 150);
  };

  useEffect(() => {
    if (!toast.autoDismiss || !toast.autoDismissMs) return;

    const startTime = toast.timestamp;
    const duration = toast.autoDismissMs;

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining > 0) {
        requestAnimationFrame(updateProgress);
      }
    };

    const animationFrame = requestAnimationFrame(updateProgress);
    return () => cancelAnimationFrame(animationFrame);
  }, [toast.autoDismiss, toast.autoDismissMs, toast.timestamp]);

  return (
    <div
      role="alert"
      aria-live={toast.severity === 'error' ? 'assertive' : 'polite'}
      className={cn(
        'relative w-80 rounded-lg border shadow-lg overflow-hidden',
        'transition-all duration-200',
        config.bgClass,
        config.borderClass,
        isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0',
      )}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', config.iconClass)} />
          <div className="flex-1 min-w-0">
            {toast.context && <p className={cn('text-xs font-medium mb-1', config.textClass)}>{toast.context}</p>}
            <p className={cn('text-sm', config.textClass)}>{toast.message}</p>
            {toast.details && toast.details.length > 0 && (
              <ul className="mt-2 space-y-1">
                {toast.details.map((detail, index) => (
                  <li key={index} className={cn('text-xs', config.textClass)}>
                    <span className="font-medium">{detail.field}:</span> {detail.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className={cn(
              'shrink-0 rounded p-1 transition-colors',
              'hover:bg-black/10 dark:hover:bg-bg-elevated/10',
              config.textClass,
            )}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {toast.autoDismiss && (
        <div className="h-1 w-full bg-black/5 dark:bg-bg-elevated/5">
          <div className={cn('h-full transition-none', config.progressClass)} style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}
